import React, { useEffect, useRef } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { LoginPage } from './LoginPage';
import { RegisterPage } from './RegisterPage';
import { ForgotPasswordPage } from './ForgotPasswordPage';

export type AuthMode = 'login' | 'register' | 'forgot';

interface AuthModalProps {
  isOpen: boolean;
  mode: AuthMode;
  onClose: () => void;
  onSwitchMode: (mode: AuthMode) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSwitchMode,
}) => {
  const backdropRef = useRef<HTMLDivElement>(null);

  // Fermer le modal sur appui de la touche Échap
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Verrouiller le défilement de la page en arrière-plan
  useEffect(() => {
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) {
          onClose();
        }
      }}
      className="fixed inset-0 z-[100] bg-slate-950/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 overflow-hidden animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-[410px] my-auto bg-white rounded-3xl shadow-2xl border-0 p-5 sm:p-6 overflow-hidden animate-in zoom-in-95 duration-150 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Barre supérieure : Logo, Titre & Bouton Fermer (Sans border) */}
        <div className="flex items-center justify-between pb-2 mb-1.5 border-0 shrink-0">
          <div className="flex items-center gap-2">
            <img src="/logo.webp" alt="Zeyilo" className="h-6 sm:h-7 w-auto object-contain" />
            <span className="text-slate-300">|</span>
            <span className="text-xs font-semibold text-slate-700">
              {mode === 'login' && 'Connexion'}
              {mode === 'register' && 'Inscription rapide'}
              {mode === 'forgot' && 'Récupération'}
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer border-0"
            aria-label="Fermer la fenêtre d'authentification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Bouton retour si mot de passe oublié */}
        {mode === 'forgot' && (
          <div className="mb-2 shrink-0">
            <button
              type="button"
              onClick={() => onSwitchMode('login')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer py-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Retour à la connexion</span>
            </button>
          </div>
        )}

        {/* Corps du formulaire (Sans scroll) */}
        <div className="w-full overflow-hidden flex-1">
          {mode === 'login' && (
            <LoginPage
              isModal
              onClose={onClose}
              onGoRegister={() => onSwitchMode('register')}
              onGoForgot={() => onSwitchMode('forgot')}
            />
          )}

          {mode === 'register' && (
            <RegisterPage
              isModal
              onClose={onClose}
              onGoLogin={() => onSwitchMode('login')}
            />
          )}

          {mode === 'forgot' && (
            <ForgotPasswordPage
              isModal
              onClose={onClose}
              onGoLogin={() => onSwitchMode('login')}
            />
          )}
        </div>
      </div>
    </div>
  );
};
